// import { ImportProfile } from "../profiles/importProfiles"
// import { columnDescription } from "@/components/import/ProfileSelector"
import { LanguageCodeType } from "@/context/SessionContext";
import { StaticDataType } from "@/utils/hooks/static/staticHooks";
import { importMatchCountryStateType } from "@/utils/serverActions/import/importActions";
import stringSimilarity from 'string-similarity';
import { columnTypeErrors, type ColumnValidationType, validateColumn } from "./columnValidations";


export type ValidationResultType = {
  isValid: boolean
  errors: Record<string, string>
  missingColumns?: string[]
  duplicateColumns?: string[]
}

export type ValidationServiceType = {
  validateRow: (row: Record<string, unknown>, fileErrors?: FileLevelErrorType[]) => ValidationResultType
  validateFile: (rows: Record<string, unknown>[]) => ValidationResult
}

export type FileLevelErrorType = {
  type: "missing" | "duplicate" | "error";
  columns: string[];
  message: string;
};

export type RowLevelErrorType = {
  row: number;
  field: string;
  error: string;
};

export type ValidationResult = {
  fileErrors: FileLevelErrorType[];
  rowErrors: RowLevelErrorType[];
  validData: Record<string, unknown>[];
};

// export type RatePeriodType = {
//   label: string;
//   value: number;
// }

// export type InvoiceTimeType = {
//   label: string;
//   value: number;
// }

// export type InvoiceModeType = {
//   label: string;
//   value: number;
// }

// export type InvoiceFrequencyType = {
//   label: string;
//   value: number;
// }

export type ImportStaticDataType = {
  ratePeriod: {
    data: StaticDataType[];
  };
  invoiceTimeMode: {
    data: StaticDataType[];
  };
  invoiceMode: {
      data: StaticDataType[];
  };
  invoiceFrequency: {
    data: StaticDataType[];
  };
  /** Plan limit import_schedule_demand_slots_per_day; null or missing = no limit. */
  scheduleSlotsPerDay?: number | null;
}

export const createValidationService = (
  profile: Record<string, ColumnValidationType>, 
  languageCode: LanguageCodeType, 
  countryStateData?: importMatchCountryStateType[] | null,
  staticData?: ImportStaticDataType,
  atLeastOneOf?: string[][]
): ValidationServiceType => {
  
    // console.log("%c dataCountryState", "background-color: red; color: white; font-weight: bold;", {countryStateData, profile, languageCode});

  
    // Check for missing columns at the file level

  const checkColumnIssues = (rows: Record<string, unknown>[]): { 
    missingColumns: string[], 
    duplicateColumns: string[] 
  } => {
    if (rows.length === 0) return { missingColumns: [], duplicateColumns: [] }
    
    const duplicateColumns = new Set<string>()
    const normalizedColumns = new Map<string, string[]>() // Maps normalized to array of original column names
    
    // First pass: collect all normalized column names
    rows.forEach(row => {
      Object.keys(row).forEach(col => {
        const normalizedCol = col.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        if (!normalizedColumns.has(normalizedCol)) {
          normalizedColumns.set(normalizedCol, [col])
        } else {
          normalizedColumns.get(normalizedCol)?.push(col)
        }
      })
    })

    // Second pass: identify actual duplicates
    normalizedColumns.forEach((originalNames) => {
      const hasRenamedColumns = originalNames.some(name => name.match(/_\d+$/))
      
      if (hasRenamedColumns) {
        const originalName = originalNames[0].split('_')[0]
        if (originalName) {
          // Find the column in the profile and get its localized name
          const columnValidation = Object.entries(profile).find(([key]) => 
            key.toLowerCase() === originalName.toLowerCase()
          )?.[1]
          
          const localizedName = columnValidation?.fieldHeader?.[languageCode] || 
            columnValidation?.fieldHeader?.en || 
            originalName
          
          duplicateColumns.add(localizedName)
        }
      }
    })

    // Check for missing required columns
    const requiredColumns = Object.entries(profile)
      .filter(([_, validation]) => validation.required)
      .map(([columnName, validation]) => 
        validation.fieldHeader?.[languageCode] || 
        validation.fieldHeader?.en || 
        columnName
      )

   

    const missingColumns = requiredColumns.filter(columnName => {
      const normalizedRequired = columnName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      return !normalizedColumns.has(normalizedRequired)
    })
    
    return {
      missingColumns,
      duplicateColumns: Array.from(duplicateColumns)
    }
  }


  const service: ValidationServiceType = {

        validateRow: (row: Record<string, unknown>, fileErrors?: FileLevelErrorType[]): ValidationResultType => {
          console.log("%c row", "background-color: red; color: white; font-weight: bold;", row)
          const errors: Record<string, string> = {}
          let isValid = true
          

          // Create a mapping of column names to their values
          const rowData = new Map<string, unknown>()
          Object.entries(row).forEach(([key, value]) => {
            rowData.set(key.toLowerCase(), value)
          })

          // Get columns that have file-level errors
          const columnsWithFileErrors = new Set<string>()
          fileErrors?.forEach(error => {
            if (error.type === 'missing' || error.type === 'duplicate') {
              error.columns.forEach(col => columnsWithFileErrors.add(col.toLowerCase()))
            }
          })

          for (const [columnName, validation] of Object.entries(profile)) {
            const fieldHeader = validation.fieldHeader?.[languageCode] || 
              validation.fieldHeader?.en || 
              columnName

            // Skip validation if the column has file-level errors
            if (columnsWithFileErrors.has(fieldHeader.toLowerCase())) {
              continue
            }

            const value = rowData.get(fieldHeader.toLowerCase())
            // console.log("%c value", "background-color: green; color: white; font-weight: bold;", {value, fieldHeader})

            // if (value !== undefined) {
              // Special handling for country/state validation
              if (columnName === 'country' || columnName === 'state') {
                const countryFieldHeader = profile.country?.fieldHeader?.[languageCode] || 
                  profile.country?.fieldHeader?.en || 
                  'country';
                const stateFieldHeader = profile.state?.fieldHeader?.[languageCode] || 
                  profile.state?.fieldHeader?.en || 
                  'state';
                
                // Get country value using the correct field header
                const countryValue = rowData.get(countryFieldHeader.toLowerCase());
                const stateValue = rowData.get(stateFieldHeader.toLowerCase());

                // Country validation
                if (columnName === 'country' && countryValue) {
                
                  // find the country match by similarity
                  const threshold = 0.7;
                  const countryMatch = countryStateData?.find(match =>
                    stringSimilarity.compareTwoStrings(
                      (countryValue as string)?.toLowerCase() ?? '',
                      (match.country_found ?? '').toLowerCase()
                    ) >= threshold
                  );

                  // console.log("%c countryMatch", "background-color: green; color: white; font-weight: bold;", {countryMatch, countryValue, countryStateData})

                  if (!countryMatch?.country_found) {
                    isValid = false;
                    errors[fieldHeader] = `País inválido: ${countryValue}`;
                  }
                }

                // State validation
                if (columnName === 'state' && stateValue) {
                  
                  // find the country match by similarity
                    const threshold = 0.7;
                    const countryMatch = countryStateData?.find(match =>
                      stringSimilarity.compareTwoStrings(
                        (countryValue as string)?.toLowerCase() ?? '',
                        (match.country_found ?? '').toLowerCase()
                      ) >= threshold
                    );

                  if (!countryMatch?.country_found) {
                    isValid = false;
                    errors[fieldHeader] = `Estado es inválido porque ${countryValue} es un país inválido`;
                  } else {
                    const normalizedStateValue = (stateValue as string).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                    const hasValidState = countryMatch.states_found.some(state => {
                      const normalizedState = state.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                      return normalizedState.includes(normalizedStateValue) || normalizedStateValue.includes(normalizedState);
                    });

                    if (!hasValidState) {
                      isValid = false;
                      errors[fieldHeader] = `Estado inválido para ${countryMatch.country_found}. Estados válidos son: ${countryMatch.states_found.join(', ')}`;
                    }
                  }
                }
              }
            
              const result = validateColumn(columnName, value, validation, staticData)
              if (!result.isValid) {

                const columnType = validation.type;
                const error = `  ${columnTypeErrors[columnType]?.[languageCode] || ""}.`;
                const customError = `  ${
                  validation.customError?.[languageCode] || ""
                }.`;

                isValid = false
                errors[fieldHeader] = (`${result.error}. 
                  ${error} ${customError}`) || "Invalid value"
              }
            // }
          }

          // Groups of columns where at least one must have a value. The error is a
          // general one of the row: it belongs to none of the columns alone.
          atLeastOneOf?.forEach((group) => {
            const headers = group
              .filter((columnName) => profile[columnName])
              .map((columnName) =>
                profile[columnName].fieldHeader?.[languageCode] ||
                profile[columnName].fieldHeader?.en ||
                columnName
              )
            if (headers.length === 0) return
            const filled = headers.some((header) => {
              const value = rowData.get(header.toLowerCase())
              return value !== undefined && value !== null && String(value).trim() !== ""
            })
            if (!filled) {
              isValid = false
              errors.General = `Falta ${headers.join(" o ")}: indica al menos uno.`
            }
          })

          return { isValid, errors }
        },

    validateFile: (rows: Record<string, unknown>[]): ValidationResult => {
      const fileErrors: FileLevelErrorType[] = [];
      const rowErrors: RowLevelErrorType[] = [];
      const validData: Record<string, unknown>[] = [];

      // Check for column-level issues first
      const { missingColumns, duplicateColumns } = checkColumnIssues(rows);

      // Add file-level errors if any
      if (missingColumns.length > 0) {
        fileErrors.push({
          type: "missing",
          columns: missingColumns,
          message: "Columnas requeridas no encontradas en el archivo",
        });
      }

      if (duplicateColumns.length > 0) {
        fileErrors.push({
          type: "duplicate",
          columns: duplicateColumns,
          message: "Columnas duplicadas encontradas en el archivo",
        });
      }

      // Pass fileErrors to validateRow
      rows.forEach((row, index) => {
        const result = service.validateRow(row, fileErrors);
        if (!result.isValid) {
          Object.entries(result.errors).forEach(([field, error]) => {
            rowErrors.push({
              row: index + 1,
              field,
              error,
            });
          });
        }
        validData.push(row);
      });
  
      return {
        fileErrors,
        rowErrors,
        validData,
      };
    },
  }

  return service;
}